import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { FiMail, FiLock, FiCheckCircle, FiClock, FiBarChart2, FiShield } from 'react-icons/fi';

const LandingPage = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/login`,
        { email, password }
      );

      const { token, user } = response.data;
      login(user, token);
      navigate('/dashboard-cliente');
    } catch (err) {
      setError(err.response?.data?.error || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = () => {
    navigate('/admin-login');
  };

  const benefits = [
    {
      icon: <FiCheckCircle className="w-8 h-8" />,
      title: 'Prenotazioni Semplici',
      description: 'Prenota carico e scarico in pochi click'
    },
    {
      icon: <FiClock className="w-8 h-8" />,
      title: 'Gestione del Tempo',
      description: 'Visualizza slot disponibili in tempo reale'
    },
    {
      icon: <FiBarChart2 className="w-8 h-8" />,
      title: 'Storico Completo',
      description: 'Monitora tutte le tue prenotazioni'
    },
    {
      icon: <FiShield className="w-8 h-8" />,
      title: 'Sicuro e Affidabile',
      description: 'Dati protetti con crittografia'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Navigation Bar */}
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        {/* Main Section */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          {/* Left Column - Graphics & Benefits */}
          <div className="order-2 md:order-1">
            {/* Hero Illustration Area */}
            <div className="mb-12">
              <div className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl p-12 text-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
                
                <div className="relative">
                  <img 
                    src="/images/logo.webp" 
                    alt="EcoPrenota Logo" 
                    className="w-80 mx-auto mb-6 drop-shadow-lg"
                  />
                  <h2 className="text-3xl font-bold text-gray-800 mb-2">
                    Prenotazione accessi
                  </h2>
                  <p className="text-gray-600">
                    Impianto di gestione sostenibile
                  </p>
                </div>
              </div>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-2 gap-4">
              {benefits.map((benefit, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl p-5 shadow-md hover:shadow-lg hover:scale-105 transition transform duration-300 border border-gray-100"
                >
                  <div className="text-green-600 mb-3 flex justify-center">
                    {benefit.icon}
                  </div>
                  <h4 className="font-bold text-gray-800 text-sm mb-1">
                    {benefit.title}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Login Form */}
          <div className="order-1 md:order-2">
            {/* Form Card */}
            <div className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100">
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  Accedi al tuo account
                </h1>
                <p className="text-gray-600">
                  Gestisci le tue prenotazioni in modo semplice e veloce
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-200 text-red-700 px-5 py-4 rounded-xl mb-6 flex items-start gap-3">
                  <span className="text-xl mt-1">⚠️</span>
                  <div>
                    <p className="font-semibold text-sm">Errore di accesso</p>
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-5">
                {/* Email Input */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-sm">
                    📧 Email
                  </label>
                  <div className="relative">
                    <FiMail className="absolute left-4 top-4 text-gray-400" size={20} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nome@azienda.com"
                      required
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition bg-gray-50 focus:bg-white"
                    />
                  </div>
                </div>

                {/* Password Input */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-sm">
                    🔐 Password
                  </label>
                  <div className="relative">
                    <FiLock className="absolute left-4 top-4 text-gray-400" size={20} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition bg-gray-50 focus:bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3 px-4 rounded-xl transition transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl mt-8"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin">⏳</span> Accesso in corso...
                    </span>
                  ) : (
                    'Accedi'
                  )}
                </button>
              </form>

              {/* Divider */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t-2 border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-gray-600">oppure</span>
                </div>
              </div>

              {/* Registration Link */}
              <div className="text-center mb-6">
                <p className="text-gray-600 text-sm">
                  Non hai un account?{' '}
                  <button
                    onClick={() => navigate('/register')}
                    className="text-green-600 font-bold hover:text-green-700 underline transition"
                  >
                    Registrati ora
                  </button>
                </p>
              </div>
            </div>

            {/* Admin Login Section */}
            <div className="mt-8 p-6 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl text-white">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="font-bold mb-1">Sei un amministratore?</h3>
                  <p className="text-sm text-gray-300">Accedi alla dashboard di gestione</p>
                </div>
                <button
                  onClick={handleAdminLogin}
                  className="px-6 py-3 bg-white text-gray-800 font-bold rounded-lg hover:bg-gray-100 transition transform hover:scale-105 active:scale-95 whitespace-nowrap"
                >
                  🔐 Admin
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-200 pt-8 mt-20">
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              © 2026 EcoPrenota • Sistema di gestione sostenibile per impianti di riciclo
            </p>
            <p className="text-gray-400 text-xs mt-3">
              Protezione dei dati • Sicurezza garantita • Disponibile 24/7
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage;
